/**
 * Extension Authentication System
 *
 * This module handles OAuth2 PKCE-based authentication between the Cloaked web dashboard
 * and the browser extension. It provides a secure, iframe-based authentication flow that
 * allows the extension to obtain access tokens without compromising user credentials.
 *
 * This is an update from the previous implementation which required the user to
 * reauthenticate via the login page.
 *
 * ARCHITECTURAL FLOW:
 * ==================
 *
 * 1. INITIATION
 *    - Extension by visiting /extension-auth/issue or requesting
 *      authentication via messaging system
 *    - Dashboard validates environment (extension installed, config present)
 *    - PKCE challenge/verifier pair is generated and stored in sessionStorage
 *
 * 2. OAUTH SETUP
 *    - Hidden iframe is created and pointed to OAuth authorization URL
 *    - Authorization URL includes PKCE challenge, extension client ID, and callback state
 *    - Message listener is registered to handle iframe communication
 *
 * 3. USER AUTHENTICATION
 *    - User authenticates in the iframe (if not already logged in)
 *    - OAuth server redirects to dashboard with authorization code
 *    - Iframe receives the callback and posts message to parent window
 *
 * 4. TOKEN EXCHANGE
 *    - Parent window extracts auth code from iframe message
 *    - Auth code is exchanged for access/refresh tokens using PKCE verifier
 *    - Tokens are stored in Vuex store for extension use
 *
 * 5. COMPLETION
 *    - Success/failure is broadcasted to extension via messaging system
 *    - Optional status page navigation (skipped in silent mode)
 *    - Resources (iframe, listeners, session data) are cleaned up
 *
 * SECURITY CONSIDERATIONS:
 * =======================
 *
 * - PKCE (Proof Key for Code Exchange) prevents authorization code interception
 * - iframe isolation prevents direct access to OAuth credentials
 * - Origin validation ensures messages only come from trusted sources
 * - Session storage is used for temporary PKCE data (auto-clears on page refresh)
 * - Silent mode prevents UI disruption for background authentication
 * - Timeout handling prevents hung authentication flows
 *
 * KEY COMPONENTS:
 * ==============
 *
 * - initializeExtensionOAuthFlow(): Main entry point for starting auth
 * - handleAuthCallback(): Processes OAuth callbacks in iframe context
 * - Message handlers: Process communication between iframe and parent
 * - PKCE utilities: Generate and validate cryptographic challenges
 * - Resource cleanup: Ensures no memory leaks or dangling references
 *
 * MODES OF OPERATION:
 * ==================
 *
 * - Silent Mode: No UI shown, used for background token refresh
 * - Interactive Mode: Shows status pages, used for user-initiated auth
 * - Error Handling: Graceful degradation with user-friendly error messages
 *
 * @author @abhijay_cloaked (abhijay@cloaked.com)
 */

//#region Imports and Types
import type { LocationQuery } from "vue-router";
import type { Router } from "vue-router";
import store from "@/store";
import { generatePkceRequirements } from "@/scripts/actions/auth";
import { HAS_ACTIVATED_PLUGIN } from "@/scripts/userFlags";

export interface ProcessAuthCodeResult {
  success: boolean;
  error?: string;
}

export interface ExtensionAuthOptions {
  silent?: boolean;
}
//#endregion

//#region Module State and Constants
let activeIframe: HTMLIFrameElement | null = null;
let activeMessageListener: ((_event: MessageEvent) => void) | null = null;

const PKCE_KEYS = {
  VERIFIER: "extension_pkce_verifier",
  CHALLENGE: "extension_pkce_challenge",
  REDIRECT_URI: "extension_pkce_intended_redirect_uri",
} as const;

const SESSION_KEYS = {
  ALLOW_NAV: "allow_extension_auth_status_navigation",
} as const;
//#endregion

//#region Cleanup and Resource Management
const cleanupOAuthResources = () => {
  if (activeIframe?.parentNode) {
    activeIframe.parentNode.removeChild(activeIframe);
  }
  activeIframe = null;

  if (activeMessageListener) {
    window.removeEventListener("message", activeMessageListener);
  }
  activeMessageListener = null;
};

const cleanupSessionStorage = () => {
  Object.values(PKCE_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
  });
};
//#endregion

//#region Navigation and Status Management
const navigateToStatusPage = (
  router: Router,
  query: Record<string, string | undefined>,
  silent: boolean = true
) => {
  if (silent) {
    return;
  }

  sessionStorage.setItem(SESSION_KEYS.ALLOW_NAV, "true");
  router.replace({ path: "/extension-auth-status", query });
};

//#endregion

//#region Validation and Environment Checks
const validateEnvironment = (): void => {
  const extensionInstalled =
    store.getters.getFlag(HAS_ACTIVATED_PLUGIN) ||
    store.getters["browser/pluginDetected"];

  if (!extensionInstalled) {
    console.error("Extension is not installed.");
    throw new Error("Extension is not installed.");
  }

  if (!import.meta.env.VITE_API) {
    console.error("API base URL not configured.");
    throw new Error("API base URL not configured.");
  }

  if (!import.meta.env.VITE_EXTENSION_CLIENT_ID) {
    console.error("Extension client ID not configured.");
    throw new Error("Extension client ID not configured.");
  }
};

const checkForExistingAuth = (): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const authCode = urlParams.get("code");
  const errorParam = urlParams.get("error");

  const appRedirectUri =
    import.meta.env.VITE_REDIRECT_URI || window.location.origin;

  if (authCode) {
    console.error(
      `This page should not receive an authorization code directly. The OAuth callback should be to ${appRedirectUri}.`
    );
    throw new Error(
      `This page should not receive an authorization code directly. The OAuth callback should be to ${appRedirectUri}.`
    );
  }

  if (errorParam) {
    const errorDescription = urlParams.get("error_description");
    console.error(
      errorDescription
        ? `OAuth error: ${errorParam} - ${errorDescription}`
        : `OAuth error: ${errorParam}`
    );
    throw new Error(
      errorDescription
        ? `OAuth error: ${errorParam} - ${errorDescription}`
        : `OAuth error: ${errorParam}`
    );
  }
};
//#endregion

//#region PKCE and Session Management
const setupPkceAndSession = async (): Promise<{
  verifier: string;
  challenge: string;
}> => {
  const [verifier, challenge] = await generatePkceRequirements();

  if (!challenge || !verifier) {
    console.error("Failed to generate PKCE parameters.");
    throw new Error("Failed to generate PKCE parameters.");
  }

  sessionStorage.setItem(PKCE_KEYS.VERIFIER, verifier);
  sessionStorage.setItem(PKCE_KEYS.CHALLENGE, challenge);
  sessionStorage.removeItem(PKCE_KEYS.REDIRECT_URI);

  return { verifier, challenge };
};
//#endregion

//#region OAuth URL and Token Processing
const createAuthUrl = (challenge: string): string => {
  const apiBaseUrl = import.meta.env.VITE_API;
  const extensionClientId = import.meta.env.VITE_EXTENSION_CLIENT_ID;
  const appRedirectUri =
    import.meta.env.VITE_REDIRECT_URI || window.location.origin;

  const authUrl = new URL(`${apiBaseUrl}o/authorize/`);
  const params = {
    response_type: "code",
    client_id: extensionClientId!,
    redirect_uri: appRedirectUri,
    scope: "read write",
    code_challenge: challenge,
    code_challenge_method: "S256",
    state: "extension_auth_callback",
  };

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      authUrl.searchParams.append(key, value);
    }
  });

  return authUrl.toString();
};

const processAuthCode = async (
  authCode: string,
  verifier: string,
  redirectUri: string
): Promise<ProcessAuthCodeResult> => {
  try {
    if (!authCode || !verifier || !redirectUri) {
      console.error("Missing required parameters for token exchange.");
      throw new Error("Missing required parameters for token exchange.");
    }

    const extensionClientId = import.meta.env.VITE_EXTENSION_CLIENT_ID;
    if (!extensionClientId) {
      console.error("Extension client ID not configured.");
      throw new Error("Extension client ID not configured.");
    }

    const payload = {
      auth: {
        [extensionClientId]: {
          code: authCode,
          redirect_uri: redirectUri,
        },
      },
    };

    await store.dispatch("authentication/setExtensionToken", {
      payload,
      codeVerifier: verifier,
      client_id: extensionClientId,
    });

    const storedAuth = store.state.authentication.extension;
    if (!storedAuth?.access_token) {
      console.error("Extension tokens not found in store after dispatch.");
      throw new Error("Extension tokens not found in store after dispatch.");
    }

    return { success: true };
  } catch (err: any) {
    const errorMessage = err || "Failed to process authorization code.";
    const finalError = `Token exchange failed: ${errorMessage}`;
    return { success: false, error: finalError };
  } finally {
    cleanupSessionStorage();
  }
};
//#endregion

//#region Message Handling and Event Processing
const createMessageHandler = (
  router: Router,
  options: ExtensionAuthOptions = {}
) => {
  const { silent } = options;

  return async (event: MessageEvent) => {
    const appRedirectUri =
      import.meta.env.VITE_REDIRECT_URI || window.location.origin;
    const expectedOrigin = new URL(appRedirectUri).origin;

    if (
      event.origin !== expectedOrigin ||
      event.data?.type !== "extensionAuthCallbackData" ||
      event.source !== activeIframe?.contentWindow
    ) {
      return;
    }
    const { code: authCode, oauthError, oauthErrorDescription } = event.data;
    cleanupOAuthResources();

    if (oauthError) {
      const errorMsg = `OAuth error: ${oauthError} - ${oauthErrorDescription || "No description"}`;
      navigateToStatusPage(router, { error: errorMsg }, silent);
      return;
    }

    if (authCode) {
      await handleAuthCodeFromIframe(authCode, router, silent);
    } else {
      const errorMsg =
        "Iframe reported callback without code or explicit OAuth error.";
      navigateToStatusPage(router, { error: errorMsg }, silent);
    }
  };
};

const handleAuthCodeFromIframe = async (
  authCode: string,
  router: Router,
  silent: boolean = true
): Promise<void> => {
  const storedVerifier = sessionStorage.getItem(PKCE_KEYS.VERIFIER);
  const appRedirectUri =
    import.meta.env.VITE_REDIRECT_URI || window.location.origin;

  if (!storedVerifier) {
    const errorMsg = "PKCE verifier not found in session.";
    navigateToStatusPage(router, { error: errorMsg }, silent);
    return;
  }

  const result = await processAuthCode(
    authCode,
    storedVerifier,
    appRedirectUri
  );

  if (result.success) {
    navigateToStatusPage(router, { success: "true" }, silent);
  } else {
    navigateToStatusPage(router, { error: result.error }, silent);
  }
};
//#endregion

//#region Iframe Management
const createAuthIframe = (authUrl: string): void => {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.addEventListener("error", (error) => {
    console.error("Iframe loading error:", error);
  });

  iframe.src = authUrl;
  document.body.appendChild(iframe);
  activeIframe = iframe;
};

const handleIframeCallback = (query: LocationQuery): void => {
  const { code: authCode, state, error: oauthError, error_description } = query;

  const messagePayload = {
    type: "extensionAuthCallbackData",
    code: authCode,
    state,
    oauthError,
    oauthErrorDescription: error_description,
  };

  const appRedirectUri =
    import.meta.env.VITE_REDIRECT_URI || window.location.origin;
  const parentOrigin = new URL(appRedirectUri).origin;
  window.parent.postMessage(messagePayload, parentOrigin);
};
//#endregion

//#region Public API
export const initializeExtensionOAuthFlow = async (
  router: Router,
  options: ExtensionAuthOptions = { silent: true }
): Promise<void> => {
  const { silent } = options;

  cleanupOAuthResources();

  try {
    validateEnvironment();
    checkForExistingAuth();

    const { challenge } = await setupPkceAndSession();

    activeMessageListener = createMessageHandler(router, options);
    window.addEventListener("message", activeMessageListener);

    const authUrl = createAuthUrl(challenge);
    const appRedirectUri =
      import.meta.env.VITE_REDIRECT_URI || window.location.origin;
    sessionStorage.setItem(PKCE_KEYS.REDIRECT_URI, appRedirectUri);

    createAuthIframe(authUrl);

    setTimeout(() => {
      if (activeIframe) {
        const timeoutError =
          "OAuth flow timeout - authentication may have failed or user closed the popup";
        cleanupOAuthResources();

        if (!silent) {
          navigateToStatusPage(router, { error: timeoutError }, silent);
        }
      }
    }, 15000);
  } catch (err: any) {
    cleanupOAuthResources();
    throw err;
  }
};

export const handleAuthCallback = async (
  query: LocationQuery
): Promise<void> => {
  const state = query.state as string;

  if (window.self !== window.top && state === "extension_auth_callback") {
    handleIframeCallback(query);
    return;
  }
};
//#endregion
